/**
 * API client — centralized HTTP client for all Portal-to-API communication.
 *
 * All API calls go through this client. It handles:
 *   - Base URL from environment variable (never hardcoded)
 *   - Authorization header injection (Bearer token from auth store)
 *   - X-Request-Id header for request tracing
 *   - Consistent error handling and response parsing
 *   - Silent token refresh on 401, retrying the original request once.
 *     BOLT-403 — the refresh path is TAB-SCOPED: a tab holding an
 *     impersonationSessionId calls POST /v1/auth/refresh-impersonation for
 *     exactly that session; every other tab calls POST /v1/auth/refresh
 *     (httpOnly cookie, the real human). A tab's identity therefore never
 *     changes because of something another tab did — when its impersonation
 *     session has ended, the tab visibly restores the super-admin stash
 *     instead of silently mutating. Concurrent 401s share a single refresh
 *     promise to avoid thundering herd.
 *
 * Engineers: NEVER use fetch() directly. Always use this client.
 *
 * Usage:
 *   import { apiClient } from '@/lib/api-client';
 *   const leads = await apiClient.get<Lead[]>('/v1/leads');
 *   await apiClient.post('/v1/leads', { body: newLead });
 */

import { useAuthStore, type UserRole } from '@/store/auth.js';
import {
  decodeJwtPayload,
  clearSuperAdminStash,
  restoreSuperAdminToken,
  markImpersonationEnded,
} from '@/auth/impersonation.js';
import { queryClient } from '@/lib/query-client.js';

/**
 * Exported for the one place that must BUILD an API URL rather than call it:
 * the rich-text editor stores uploaded images as
 * `${API_BASE_URL}/v1/orgs/{orgId}/uploads/{key}` so the document's <img src>
 * is directly loadable. Empty string in local dev = same-origin (vite proxy).
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string>;
  /**
   * When true, a 401 response is thrown immediately without attempting a
   * silent token refresh. Use for the logout endpoint so an expired token
   * does not trigger silentRefresh() and issue a new session mid-teardown.
   */
  skipRefresh?: boolean;
}

/**
 * Typed API error thrown when the server returns a non-2xx response.
 * Matches the API's standard error shape.
 */
/**
 * Per-field validation issue returned by the API when code === 'validation_error'.
 * `field` uses dotted paths matching the request body shape (e.g. "business.phone").
 */
export interface ApiErrorDetail {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string,
    /** Field-level validation errors when code === 'validation_error'. */
    public readonly details?: ApiErrorDetail[],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---------------------------------------------------------------------------
// Silent refresh — serialized so concurrent 401s fire only one /refresh call.
// ---------------------------------------------------------------------------

let _refreshPromise: Promise<string | null> | null = null;

function refreshUrlFor(path: string): string {
  return API_BASE_URL
    ? new URL(path, API_BASE_URL).toString()
    : new URL(path, window.location.origin).toString();
}

/**
 * Regular-session refresh: POST /v1/auth/refresh (httpOnly 97d_refresh cookie).
 * On success: adopts the response identity and returns the new access token.
 * On failure (refresh expired/invalid): clearAuth() + redirect to /login.
 */
async function refreshRegularTab(): Promise<string | null> {
  const res = await fetch(refreshUrlFor('/v1/auth/refresh'), {
    method: 'POST',
    credentials: 'include', // send the 97d_refresh httpOnly cookie
    headers: { 'Content-Type': 'application/json' },
  });

  if (!res.ok) {
    clearSuperAdminStash(); // a dead cookie session invalidates the stash too
    useAuthStore.getState().clearAuth();
    await queryClient.cancelQueries();
    queryClient.clear();
    window.location.replace('/login');
    return null;
  }

  const data = (await res.json()) as {
    access_token: string;
    user: { id: string; email: string; role: string; org_id: string | null };
  };

  // BOLT-403 mismatch guard: adopt the response identity in FULL, always.
  // The incident that motivated this bolt was a token-only swap that left the
  // store (and the rendered UI) claiming one identity while the bearer token
  // carried another — a write then lands under the wrong user. setAuth keeps
  // both in lockstep; a same-identity refresh is a no-op difference.
  useAuthStore
    .getState()
    .setAuth(data.access_token, data.user.org_id ?? '', data.user.id, data.user.role as UserRole);
  useAuthStore.getState().setTargetEmail(null);

  return data.access_token;
}

/**
 * Impersonation-session refresh — BOLT-403.
 *
 * POST /v1/auth/refresh-impersonation with the session id THIS TAB holds,
 * authenticated by the super-admin's own httpOnly cookie. A 401/403 means the
 * session is over (ended elsewhere, hard-capped, or the target became
 * ineligible): restore the tab-local super-admin stash and land on the admin
 * surface with a visible marker — never silently fall through to a different
 * identity, and never leave this tab wearing a dead session id.
 *
 * Only 401/403 are AUTHORITATIVE about the session. Anything else (503 Redis
 * blip, 500) proves nothing — tearing down a live impersonation on a
 * transient error would tell the operator their session ended while the row
 * stays active and unreachable. Those throw instead; the tab keeps its
 * identity and the next user action retries.
 */
async function refreshImpersonationTab(sessionId: string): Promise<string | null> {
  const res = await fetch(refreshUrlFor('/v1/auth/refresh-impersonation'), {
    method: 'POST',
    credentials: 'include', // 97d_refresh authenticates; session_id only selects
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ session_id: sessionId }),
  });

  if (!res.ok && res.status !== 401 && res.status !== 403) {
    throw await parseError(res);
  }

  if (res.ok) {
    const data = (await res.json()) as { access_token: string };
    const store = useAuthStore.getState();
    // Mismatch guard: the store's identity must equal the token's. They can
    // only disagree if tab state was tampered with mid-flight, but the repair
    // is one setAuth — adopt the claims rather than ship a mislabeled header.
    const claims = decodeJwtPayload<{ sub: string; org_id: string | null; role: string }>(
      data.access_token,
    );
    if (claims && claims.sub !== store.userId) {
      store.setAuth(data.access_token, claims.org_id ?? '', claims.sub, claims.role as UserRole);
    } else {
      store.setAccessToken(data.access_token);
    }
    return data.access_token;
  }

  // The impersonation is over. Which tab ended it is irrelevant — this tab's
  // identity changes VISIBLY or not at all.
  const { targetEmail } = useAuthStore.getState();
  useAuthStore.getState().setImpersonationSessionId(null);
  await queryClient.cancelQueries();
  queryClient.clear();

  const stash = restoreSuperAdminToken();
  if (stash) {
    useAuthStore.getState().setAuth(stash.accessToken, stash.orgId, stash.userId, stash.role);
    useAuthStore.getState().setTargetEmail(null);
    markImpersonationEnded(targetEmail);
    window.location.replace('/admin/orgs');
  } else {
    // No stash (e.g. a duplicated tab whose twin consumed it) — the cookie
    // session is still the super-admin's; a reload bootstraps it cleanly.
    useAuthStore.getState().clearAuth();
    markImpersonationEnded(targetEmail);
    window.location.replace('/admin/orgs');
  }
  return null;
}

/**
 * Tab-scoped silent refresh — BOLT-403. Routes to the impersonation or the
 * regular path based on what THIS TAB holds; never lets the server's ambient
 * state pick an identity for the tab.
 *
 * Multiple simultaneous callers share one in-flight promise so the cookie
 * isn't rotated more than once per expiry.
 */
async function silentRefresh(): Promise<string | null> {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async (): Promise<string | null> => {
    try {
      const { impersonationSessionId } = useAuthStore.getState();
      return impersonationSessionId
        ? await refreshImpersonationTab(impersonationSessionId)
        : await refreshRegularTab();
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

// ---------------------------------------------------------------------------
// Session bootstrap — called once per tab on app startup.
// ---------------------------------------------------------------------------

/**
 * Attempts to restore this tab's session at app startup (AuthBootstrap).
 *
 * BOLT-403: a genuinely fresh tab is born as the real human, always — the
 * pre-403 design bootstrapped whatever the ambient impersonation cookie said,
 * which made "open a new tab" inherit someone else's identity. A tab that IS
 * mid-impersonation never reaches the fetch below: its access token persists
 * in sessionStorage alongside its session id (both written atomically by the
 * store), so it returns at the fast path, and a stale token is renewed by the
 * 401 interceptor's impersonation-aware silentRefresh.
 *
 * Returns true if a session was established, false otherwise.
 * Never redirects on failure — route guards handle unauthenticated state.
 * (Contrast with silentRefresh(), which redirects on failure because it
 * fires mid-session when a valid cookie is expected.)
 */
export async function bootstrapSession(): Promise<boolean> {
  const { accessToken, setAuth } = useAuthStore.getState();
  if (accessToken) return true; // fast-path: same-tab page refresh

  try {
    const res = await fetch(refreshUrlFor('/v1/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!res.ok) return false;

    const data = (await res.json()) as {
      access_token: string;
      user: { id: string; email: string; role: string; org_id: string | null };
    };

    setAuth(data.access_token, data.user.org_id ?? '', data.user.id, data.user.role as UserRole);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Core request
// ---------------------------------------------------------------------------

function buildUrl(path: string, params?: Record<string, string>): URL {
  const url = API_BASE_URL ? new URL(path, API_BASE_URL) : new URL(path, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
  }
  return url;
}

async function parseError(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => ({
    error: 'unknown_error',
    message: response.statusText,
    request_id: 'unknown',
  }));
  // `details` is an optional array of { field, message } pairs for 400 validation_error responses.
  const details = Array.isArray(body.details) ? (body.details as ApiErrorDetail[]) : undefined;
  return new ApiError(response.status, body.error, body.message, body.request_id, details);
}

async function request<T>(method: string, path: string, options?: RequestOptions): Promise<T> {
  const url = buildUrl(path, options?.params);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  const token = useAuthStore.getState().accessToken;
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const fetchOptions: RequestInit = {
    method,
    headers,
    body: options?.body ? JSON.stringify(options.body) : undefined,
    credentials: 'include',
  };

  const response = await fetch(url.toString(), fetchOptions);

  // On 401: attempt silent refresh then retry the original request once.
  // skipRefresh callers (e.g. logout) receive the 401 directly so the
  // interceptor does not issue a new session mid-teardown.
  if (response.status === 401 && !options?.skipRefresh) {
    const newToken = await silentRefresh();
    if (!newToken) {
      // silentRefresh already redirected to /login
      throw new ApiError(
        401,
        'unauthorized',
        'Session expired. Please log in again.',
        'refresh_failed',
      );
    }

    const retryHeaders = { ...headers, Authorization: `Bearer ${newToken}` };
    const retryResponse = await fetch(url.toString(), {
      ...fetchOptions,
      headers: retryHeaders,
    });

    if (!retryResponse.ok) throw await parseError(retryResponse);
    if (retryResponse.status === 204) return undefined as T;
    return retryResponse.json() as Promise<T>;
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/**
 * API client with typed HTTP methods.
 * All methods return parsed JSON or throw ApiError.
 */
export const apiClient = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>) =>
    request<T>('GET', path, options),

  post: <T>(path: string, options?: RequestOptions) => request<T>('POST', path, options),

  put: <T>(path: string, options?: RequestOptions) => request<T>('PUT', path, options),

  patch: <T>(path: string, options?: RequestOptions) => request<T>('PATCH', path, options),

  // A body is permitted on DELETE (RFC 9110; Express parses it) and BOLT-370
  // needs one: the DNS delete carries `expected_values` as a compare-and-set,
  // and those are whole record sets — a single DKIM TXT runs past 400 bytes, so
  // a query string would hit URL length limits on a large set.
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
};
